/**
 * Nova Proactive Reach-Out — Milestone Trigger Rules
 *
 * 18 rules total: 3 per chapter (cycle, ivf, ttc, pregnancy, postpartum, menopause).
 * Priority order when multiple fire same day: PREPARATORY > EMOTIONAL > MILESTONE > PATTERN > SEASONAL.
 */

export const TRIGGER_TYPES = {
  PREPARATORY: 5,
  EMOTIONAL: 4,
  MILESTONE: 3,
  PATTERN: 2,
  SEASONAL: 1,
};

/**
 * @typedef {Object} TriggerRule
 * @property {string} chapter
 * @property {string} type - PREPARATORY | EMOTIONAL | MILESTONE | PATTERN | SEASONAL
 * @property {function} condition_fn - (ctx) => boolean
 * @property {string} message_id
 * @property {boolean} [is_repeatable]
 * @property {number} [cooldown_days]
 */

function daysSince(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((d - Date.now()) / (1000 * 60 * 60 * 24));
}

function pregnancyWeek(dueDateStr) {
  if (!dueDateStr) return null;
  const due = new Date(dueDateStr);
  if (Number.isNaN(due.getTime())) return null;
  const now = new Date();
  const elapsed = (now - due) / (1000 * 60 * 60 * 24);
  return Math.floor(40 - (elapsed / 7));
}

function childAgeMonths(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  return (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
}

/** 18 milestone rules — 3 per chapter */
export const MILESTONE_TRIGGER_RULES = [
  // ── CYCLE (First Chapter) ───────────────────────────────────
  {
    chapter: 'cycle',
    type: 'PREPARATORY',
    condition_fn: (ctx) => {
      const last = ctx.timeline?.cycle_data?.last_period_date;
      if (!last) return false;
      const since = daysSince(last);
      const avg = ctx.timeline?.cycle_data?.avg_cycle_length || 28;
      return since >= avg - 2 && since <= avg + 2;
    },
    message_id: 'cycle_period_expected',
    is_repeatable: true,
    cooldown_days: 28,
  },
  {
    chapter: 'cycle',
    type: 'EMOTIONAL',
    condition_fn: (ctx) => {
      const logs = ctx.moodLogs || [];
      const recent = logs.filter((l) => daysSince(l.logged_at) <= 7);
      const lowMood = recent.some((l) => (l.data?.value ?? 5) <= 3);
      return lowMood && recent.length >= 2;
    },
    message_id: 'cycle_mood_checkin',
    is_repeatable: true,
    cooldown_days: 14,
  },
  {
    chapter: 'cycle',
    type: 'MILESTONE',
    condition_fn: (ctx) => {
      const start = ctx.timeline?.chapter_start_date;
      if (!start) return false;
      const since = daysSince(start);
      return since === 30;
    },
    message_id: 'cycle_one_month',
    is_repeatable: false,
    cooldown_days: 0,
  },

  // ── IVF ─────────────────────────────────────────────────────
  {
    chapter: 'ivf',
    type: 'PREPARATORY',
    condition_fn: (ctx) => {
      const retrieval = ctx.timeline?.key_dates?.retrieval_date;
      if (!retrieval) return false;
      const until = daysUntil(retrieval);
      return until === 1;
    },
    message_id: 'ivf_retrieval_tomorrow',
    is_repeatable: false,
    cooldown_days: 0,
  },
  {
    chapter: 'ivf',
    type: 'EMOTIONAL',
    condition_fn: (ctx) => {
      const transfer = ctx.timeline?.key_dates?.transfer_date;
      if (!transfer) return false;
      const since = daysSince(transfer);
      return since >= 6 && since <= 8;
    },
    message_id: 'ivf_two_week_wait',
    is_repeatable: false,
    cooldown_days: 0,
  },
  {
    chapter: 'ivf',
    type: 'MILESTONE',
    condition_fn: (ctx) => {
      const start = ctx.timeline?.key_dates?.treatment_start;
      if (!start) return false;
      const since = daysSince(start);
      return since === 14;
    },
    message_id: 'ivf_stims_week_two',
    is_repeatable: false,
    cooldown_days: 0,
  },

  // ── TTC ─────────────────────────────────────────────────────
  {
    chapter: 'ttc',
    type: 'PREPARATORY',
    condition_fn: (ctx) => {
      const last = ctx.timeline?.cycle_data?.last_period_date;
      if (!last) return false;
      const avg = ctx.timeline?.cycle_data?.avg_cycle_length || 28;
      const ovuDay = Math.max(10, Math.min(avg - 10, avg - 14));
      const fertileStart = new Date(last);
      fertileStart.setDate(fertileStart.getDate() + ovuDay - 5);
      const until = daysUntil(fertileStart.toISOString().slice(0, 10));
      return until === 0;
    },
    message_id: 'ttc_fertile_window_start',
    is_repeatable: true,
    cooldown_days: 28,
  },
  {
    chapter: 'ttc',
    type: 'EMOTIONAL',
    condition_fn: (ctx) => {
      const last = ctx.timeline?.cycle_data?.last_period_date;
      if (!last) return false;
      const since = daysSince(last);
      return since >= 12 && since <= 14;
    },
    message_id: 'ttc_ovulation_checkin',
    is_repeatable: true,
    cooldown_days: 14,
  },
  {
    chapter: 'ttc',
    type: 'MILESTONE',
    condition_fn: (ctx) => {
      const start = ctx.timeline?.chapter_start_date;
      if (!start) return false;
      const since = daysSince(start);
      return since === 90;
    },
    message_id: 'ttc_three_months',
    is_repeatable: false,
    cooldown_days: 0,
  },

  // ── PREGNANCY ───────────────────────────────────────────────
  {
    chapter: 'pregnancy',
    type: 'PREPARATORY',
    condition_fn: (ctx) => {
      const week = pregnancyWeek(ctx.timeline?.key_dates?.due_date);
      return week === 12;
    },
    message_id: 'pregnancy_12_week',
    is_repeatable: false,
    cooldown_days: 0,
  },
  {
    chapter: 'pregnancy',
    type: 'EMOTIONAL',
    condition_fn: (ctx) => {
      const week = pregnancyWeek(ctx.timeline?.key_dates?.due_date);
      return week === 20;
    },
    message_id: 'pregnancy_anatomy_scan',
    is_repeatable: false,
    cooldown_days: 0,
  },
  {
    chapter: 'pregnancy',
    type: 'MILESTONE',
    condition_fn: (ctx) => {
      const week = pregnancyWeek(ctx.timeline?.key_dates?.due_date);
      return week === 37;
    },
    message_id: 'pregnancy_full_term',
    is_repeatable: false,
    cooldown_days: 0,
  },

  // ── POSTPARTUM ──────────────────────────────────────────────
  {
    chapter: 'postpartum',
    type: 'PREPARATORY',
    condition_fn: (ctx) => {
      const youngest = ctx.timeline?.children?.[0];
      const dob = youngest?.dob;
      if (!dob) return false;
      const days = daysSince(dob);
      return days === 6;
    },
    message_id: 'postpartum_one_week',
    is_repeatable: false,
    cooldown_days: 0,
  },
  {
    chapter: 'postpartum',
    type: 'EMOTIONAL',
    condition_fn: (ctx) => {
      const youngest = ctx.timeline?.children?.[0];
      const dob = youngest?.dob;
      if (!dob) return false;
      const days = daysSince(dob);
      return days >= 40 && days <= 45;
    },
    message_id: 'postpartum_six_weeks',
    is_repeatable: false,
    cooldown_days: 0,
  },
  {
    chapter: 'postpartum',
    type: 'MILESTONE',
    condition_fn: (ctx) => {
      const youngest = ctx.timeline?.children?.[0];
      const dob = youngest?.dob;
      if (!dob) return false;
      const months = childAgeMonths(dob);
      return months === 3;
    },
    message_id: 'postpartum_three_months',
    is_repeatable: false,
    cooldown_days: 0,
  },

  // ── MENOPAUSE ───────────────────────────────────────────────
  {
    chapter: 'menopause',
    type: 'PREPARATORY',
    condition_fn: (ctx) => {
      const start = ctx.timeline?.chapter_start_date;
      if (!start) return false;
      const since = daysSince(start);
      return since === 30;
    },
    message_id: 'menopause_one_month',
    is_repeatable: false,
    cooldown_days: 0,
  },
  {
    chapter: 'menopause',
    type: 'EMOTIONAL',
    condition_fn: (ctx) => {
      const logs = ctx.hotFlashLogs || [];
      const recent = logs.filter((l) => daysSince(l.logged_at) <= 7);
      return recent.length >= 3;
    },
    message_id: 'menopause_hot_flash_pattern',
    is_repeatable: true,
    cooldown_days: 14,
  },
  {
    chapter: 'menopause',
    type: 'SEASONAL',
    condition_fn: (ctx) => {
      const month = new Date().getMonth();
      return month === 5 || month === 9;
    },
    message_id: 'menopause_seasonal_checkin',
    is_repeatable: true,
    cooldown_days: 90,
  },
];
